import { dateOf, esc, table, tile } from './sbchart.ts';

type Data = Record<string, any>;
export interface MedicalLinks { hospital(facility: Data | null | undefined): string; insurer(firm: Data | null | undefined): string }
export interface MedicalContext { personId: number; here?: boolean; opened?: ReadonlySet<string> }
const date = (value: unknown): string => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? dateOf(value) : '未记录';
const amount = (value: unknown): string => typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(2)} 元` : '未记录';
const row = (label: string, value: string): string => `<div class="sb-row"><span>${esc(label)}</span><b>${value}</b></div>`;
const section = (title: string, content: string, note = '', id = ''): string => `<section class="sb-sec"${id ? ` id="${esc(id)}"` : ''}><h4>${esc(title)}${note ? `<small>${esc(note)}</small>` : ''}</h4>${content}</section>`;
const empty = (text: string): string => `<div class="sb-empty">${esc(text)}</div>`;
const list = (value: unknown): Data[] => Array.isArray(value) ? value : [];
const hospitalRef = (value: unknown): Data | undefined => typeof value === 'number' ? { id: value, name: `医院 #${value}` } : value && typeof value === 'object' ? value as Data : undefined;

/** 仅路由到这一所医院的按需病历页,不读取冷档或把床日推算成入院次数。 */
export function hospitalHistoryLinkHTML(h: Data): string {
  const id = h.hospital?.id;
  return typeof id === 'number' && Number.isSafeInteger(id) && id >= 0
    ? `<a data-go="hospital:${id}" data-view="records">看本院已归档诊疗经过、住院与出院记录 →</a>`
    : '<span class="meta">医院编号未提供，无法打开本院诊疗档案。</span>';
}

/** WardState 为当日计数,不是累计医院史；资料缺失不能用0补齐。 */
export function hospitalActivityHTML(h: Data): string {
  const w = h.ward;
  const count = (n: unknown) => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 ? String(n) : '未记录';
  return section('资料当日 · 已完成诊疗与流转',
    row('当日入院 / 出院', `${count(w?.admitted)} / ${count(w?.discharged)} 人`) +
    row('当日门诊 / 检查 / 治疗', `${count(w?.consultations)} / ${count(w?.examinations)} / ${count(w?.treatments)} 次`) +
    '<p class="meta">当日计数每天重置；现在没有住院患者或当日入院为0，不代表以前没有发生过住院。</p>',
    h.day != null ? date(h.day) : '日期未记录') +
    section('既往医疗经历', `<p>${hospitalHistoryLinkHTML(h)}</p><p class="meta">点击后按页读取这所医院关联的真实病历与成功收据，每页最多80条。护理床日不是入院次数；完整历史未开启、晚开或旧版未保留的内容仍可能未知。</p>`) +
    '<p class="meta">当前服务有门诊、检查、治疗、住院护理、产检与分娩护理；独立手术服务尚未建模，不把治疗或床日称为手术。</p>';
}

/** 只显示真正检查给出的诊断；隐藏病种或症状相似均不能充当确诊。 */
export function medicalConditionHTML(episodes: unknown, asOf?: unknown): string {
  if (!Array.isArray(episodes)) return '病程摘要未提供，点击居民查看';
  const values = list(episodes);
  const knownDay = typeof asOf === 'number' && Number.isSafeInteger(asOf) && asOf >= 0;
  return values.length ? values.map(e => {
    const status = typeof e.statusName === 'string' && e.statusName.trim() ? e.statusName : '未记录';
    const records = list(e.lastServices), services = new Set<string>();
    let latest = -1;
    for (const s of records) {
      if (!knownDay || typeof s?.service !== 'string' || !s.service.trim() ||
        typeof s.day !== 'number' || !Number.isSafeInteger(s.day) || s.day < 0 || s.day > asOf) continue;
      if (s.day > latest) { latest = s.day; services.clear(); }
      if (s.day === latest) services.add(s.service);
    }
    const recent = latest >= 0 ? `${[...services].map(esc).join('、')} · ${date(latest)}`
      : '未记录';
    return `<b>${esc(e.diagnosis?.name || '尚未确诊')}</b><br><span class="meta">${esc(e.symptoms || '症状未记录')}</span>` +
      `<br><small class="meta" style="display:inline-block;max-width:28em;white-space:normal">状态：${esc(status)}；最近诊疗：${recent}</small>`;
  }).join('<br>') : '当前未记录显现病程';
}

function diagnosisSummary(m: Data, personId: number): string {
  const current = list(m.episodes), past = list(m.closedEpisodes);
  const names = (episodes: Data[]) => [...new Set<string>(episodes.map(e => e.diagnosis?.name).filter(name => typeof name === 'string' && name))].map(esc).join('、');
  const unknown = current.filter(e => !e.diagnosis?.name).length;
  const currentNames = names(current), pastNames = names(past);
  const prefix = `medical-${personId}`;
  return section('当前与既往病名', row('当前已确诊', currentNames ? `${currentNames}${unknown ? `；另有 ${unknown} 段尚未确诊` : ''}` : current.length ? `尚未确诊（${current.length} 段当前显现病程）` : '当前没有已显现病程') +
    row('近期既往已确诊', pastNames || (m.closedEpisodes == null ? '此版本未保留结构化摘要，查看已有病历' : '近期摘要没有保留确诊病名，可能当时未确诊')) +
    `<p class="meta">既往病名仅限最近 ${esc(m.closedLimit ?? 12)} 段摘要。</p>` +
    `<nav class="sb-related-places" aria-label="本人的医疗页导航">${[['current', '现在症状'], ['past', '既往病史'], ['bills', '检查与费用'], ['records', '近期经过'], ['archive', '完整病历']].map(([id, name]) => `<a href="#${prefix}-${id}"${id === 'archive' ? ` data-medical-archive="${personId}"` : ''}>${name}</a>`).join('')}</nav>`);
}

function currentEpisodes(m: Data, links: MedicalLinks, id: string): string {
  const episodes = list(m.episodes);
  return section('目前症状与诊断', episodes.length ? episodes.map(e =>
    `<section class="sb-sec"><h4>${esc(e.diagnosis?.name || '尚未确诊')} <small>当前病程 #${esc(e.id)}</small></h4>` +
    (e.statusName ? row('病程状态', esc(e.statusName)) : '') + row('当前症状', esc(e.symptoms || '症状未记录')) + row('出现日期', date(e.since)) +
    row('严重程度', e.severity != null ? `${esc(e.severity)} / 100（游戏模型）` : '未记录') +
    (e.diagnosis ? row('检查确诊', `${date(e.diagnosis.confirmed)} · ${links.hospital(e.diagnosis.hospital)}`) : '<p class="meta">尚未完成检查确诊。</p>') +
    lastServicesHTML(e) + '</section>').join('')
    : empty('当前没有已显现病程。'), '', id);
}

function lastServicesHTML(e: Data): string {
  const services = list(e.lastServices);
  return services.length ? table(['已完成服务', '最近一次成功日期'], services.map(s => [esc(s.service), date(s.day)])) : '<p class="meta">未记录已完成服务日期。</p>';
}

function pendingHTML(m: Data, links: MedicalLinks): string {
  const orders = list(m.pendingOrders);
  return section('治疗安排 · 待完成医嘱', orders.length ? table(['计划服务', '病程 / 医嘱', '日期', '医院'], orders.map(o => [esc(o.service), `病程 #${esc(o.episode)} · 医嘱 #${esc(o.order)}`, date(o.day), links.hospital(o.hospital)]))
    : empty(m.pendingOrders == null ? '待办医嘱列表未提供。' : '暂无待办医嘱。')) + admissionHTML(m, links);
}

function pastEpisodesHTML(m: Data, context: MedicalContext, links: MedicalLinks): string {
  const past = list(m.closedEpisodes), limit = m.closedLimit ?? 12;
  return section('既往病史 · 近期已结束病程', past.length ? [...past].sort((a, b) => (b.ended ?? -1) - (a.ended ?? -1)).map(e => {
    const key = `medical:${context.personId}:past:${e.id}`;
    return `<details data-key="${esc(key)}"${context.opened?.has(key) ? ' open' : ''}><summary><b>${esc(e.diagnosis?.name || '未确诊的不适')}</b> · ${esc(e.outcomeName || '已结束，结局未记录')} · ${date(e.ended)} · 病程 #${esc(e.id)}</summary>` +
      row('当时症状', esc(e.symptoms || '未记录')) + row('出现 / 结束', `${date(e.since)} → ${date(e.ended)}`) +
      (e.diagnosis ? row('实际检查确诊', `${esc(e.diagnosis.name)} · ${date(e.diagnosis.confirmed)} · ${links.hospital(e.diagnosis.hospital)}`) : '<p class="meta">未保留检查确诊。</p>') +
      lastServicesHTML(e) + '</details>';
  }).join('') : empty(m.closedEpisodes == null ? '结构化既往摘要未提供。' : '暂无近期已结束病程摘要。'), `已展示 ${past.length} / ${m.closedObservedCount != null ? `本城实际可见结束病程 ${m.closedObservedCount}` : '实际总量未提供'}；最近 ${limit} 个`, `medical-${context.personId}-past`);
}

function recentRecordsHTML(m: Data, links: MedicalLinks, id: string): string {
  const records = list(m.records);
  return section('近期诊疗经过', records.length ? `<ol class="sb-news">${records.map(r => `<li><time>${date(r.day)} · ${esc(r.what)}${r.episode != null ? ` · 病程 #${esc(r.episode)}` : ''}</time><p>${esc(typeof r.detail === 'string' ? r.detail : '此记录没有保留可读说明')}</p>${r.hospital ? row('诊疗医院', links.hospital(r.hospital)) : ''}</li>`).join('')}</ol>`
    : empty('暂无近期诊疗记录。'), `已展示 ${records.length} / ${m.recordCount != null ? `本城实际临床记录 ${m.recordCount} 条` : '实际总量未提供'}；最近 ${m.historyLimit ?? 48} 条`, id);
}

function admissionHTML(m: Data, links: MedicalLinks): string {
  const a = m.admission;
  return (a ? section('当前住院', row('住院医院', links.hospital(a.hospital)) + row('入院日期', date(a.since)) +
    row('最近实际护理', a.lastCare != null ? date(a.lastCare) : '尚未完成护理') +
    (a.waitingCoverage ? row('护理等待', '<span class="bad">等待有效付款或保障，床位仍保留。</span>') : ''))
    : section('当前住院', empty('暂无住院登记。'))) +
    (m.refusal ? row('最近未完成原因', `<span class="bad">${esc(m.refusal)}</span>`) : '');
}

function costsHTML(m: Data, links: MedicalLinks, id: string): string {
  const c = m.cover, bills = list(m.todayBills);
  return section('资料当日已完成的检查、治疗与收费', bills.length ? table(['已完成服务', '医院', '家庭自付', '雇主代付', '基本医保', '补充或工伤保险', '医院实收'], bills.map(b => [
    `${esc(b.service)}${b.episode != null ? `<br><small>病程 #${esc(b.episode)}${b.order != null ? ` · 医嘱 #${esc(b.order)}` : ''}${b.day != null ? ` · ${date(b.day)}` : ''}</small>` : ''}`, links.hospital(b.hospital), amount(b.patient), amount(b.employer), amount(b.public), amount(b.insurer), amount(b.total),
  ])) : empty('该日暂无已完成收费。'), `${date(m.day)} 当日真实账单`, id) +
    (c ? section('医保与累计实际缴付', row('基本医保剩余额度', `${amount(c.basicLeft)} · ${c.basicUntil > 0 && c.basicUntil >= m.day ? `有效至 ${date(c.basicUntil)}` : '当前无有效基本保障'}`) +
      row('补充医保剩余额度', `${amount(c.extraLeft)} · ${c.extraUntil > 0 && c.extraUntil >= m.day ? `保单约定至 ${date(c.extraUntil)}` : '当前无未到期补充保单'}`) +
      row('补充医保机构', c.insurer ? links.insurer(c.insurer) : '未投保') +
      row('基本医保累计缴费 / 实付', `${amount(c.contributions)} / ${amount(c.publicPaid)}`) + row('补充医保累计保费 / 理赔', `${amount(c.premiums)} / ${amount(c.claims)}`)) : '');
}

function recordScopeHTML(m: Data, context: MedicalContext): string {
  const key = `medicalScope:${context.personId}`;
  return `<details data-key="${key}"${context.opened?.has(key) ? ' open' : ''}><summary>记录范围与口径</summary><ul>` +
    `<li>没有当前症状不代表从未生病；未确诊不凭症状猜病名。既往仅最近 ${esc(m.closedLimit ?? 12)} 段摘要，不是终身患病总数；结束病程包含伤病、孕产和死亡或迁出时停止跟踪，不能当作死因或已治愈。</li>` +
    `<li>近期诊疗记录保留 ${esc(m.historyLimit ?? 48)} 条。更早未保存的诊断和结局不会从旧文字补造，出生或入城前病史可能未知；完整病历按本城实际归档范围分页读取，晚开归档或仅热模式下已丢失的记录不能恢复。</li>` +
    '<li>待办不等于已完成治疗、检查、入院或付款；无医嘱时仍可能尚未找到医院或尚未开单。最近未完成原因属于本人，不逐单猜测；已有住院者等待有效付款或保障时仍占床。</li>' +
    '<li>已完成服务只列最近一次成功日期，不是治疗、检查或护理总次数。账单只列资料当日，不是全部病程费用；医保是本人累计，保单与额度不保证赔付，实际付款以成功收据为准。</li>' +
    '<li>健康与在场标记不代表任职资格；任职仍受年龄与岗位资格等条件约束。一次临床病程结束不代表全部限制解除，仍须查看实际工伤恢复期限。</li></ul></details>';
}

export const medicalArchiveKey = (personId: number): string => `medicalArchive:${personId}`;
/** 折叠时不调用读取函数，也不生成冷记录DOM；打开后复用原有按人物/来源缓存与游标。 */
export function medicalArchiveHTML(personId: number, opened: ReadonlySet<string>, read: (kind: 'person', id: number, source: 'medical') => string): string {
  const key = medicalArchiveKey(personId), active = opened.has(key);
  return `<section class="sb-sec" id="medical-${personId}-archive"><details data-key="${key}"${active ? ' open' : ''}><summary>完整病历 · ${active ? '本人医疗归档' : '展开读取本人医疗归档'}</summary>${active ? read('person', personId, 'medical') : ''}</details></section>`;
}

export function patientMedicalHTML(m: Data | null | undefined, context: MedicalContext, links: MedicalLinks): string {
  if (!m?.enabled) return empty('此城市使用旧医疗规则，没有可供展示的当前临床病程。');
  const episodes = list(m.episodes);
  const status = context.here === false ? '不在城内，查看已留病历' : m.admission ? '住院中' : m.absent ? '医疗休养' : m.injuryRecoveryUntil != null ? '工伤恢复中' : episodes.length ? '有显现病程' : '当前未记录显现病程';
  return (m.day != null ? `<p><b>医疗资料截至 ${date(m.day)}</b>；以本页资料日期为准。</p>` : '<p class="meta">此份医疗资料没有提供截至日期，不把顶栏日期当作账单日期。</p>') +
    diagnosisSummary(m, context.personId) +
    (m.historySince != null ? row('本城首条实际临床记录', date(m.historySince)) : row('本城首条临床记录', '尚未记录或旧资料未知')) +
    `<div class="sb-grid">${tile('目前状态', esc(status))}${tile('当前显现病程', String(episodes.length), '只列已显现症状，不包含未显现病种')}</div>` +
    (m.injuryRecoveryUntil != null ? row('仍在工伤恢复期', `${date(m.injuryRecoveryUntil)} 前仍按原工伤规则养伤。`) : '') +
    (typeof m.workAvailable === 'boolean' ? row('当前健康与在场限制', m.workAvailable ? '未触发缺勤限制（非任职资格判定）' : '存在缺勤限制') : '') +
    currentEpisodes(m, links, `medical-${context.personId}-current`) + pendingHTML(m, links) + pastEpisodesHTML(m, context, links) + costsHTML(m, links, `medical-${context.personId}-bills`) + recentRecordsHTML(m, links, `medical-${context.personId}-records`) +
    recordScopeHTML(m, context);
}

/** 完整病历只读取当前人物的医疗归档；不从叙述文字解析病种或支付金额。 */
export function medicalRecordHTML(data: Data, patientId: number | undefined, links: MedicalLinks): string {
  const s = data.snapshot, bill = data.receipt;
  const paid = (value: unknown) => typeof value === 'number' ? amount(value / 100) : '未记录';
  const provider = typeof bill?.provider === 'number' && Number.isInteger(bill.provider) && bill.provider >= 0 && bill.provider <= 0xffffffff
    ? `<a data-go="firm:${bill.provider}">公司 #${bill.provider}</a>` : '未记录';
  return (patientId != null ? row('患者', `<a data-go="person:${patientId}" data-view="medical">人物 #${patientId}</a>`) : '') +
    (data.episode != null ? row('病程编号', `#${esc(data.episode)}`) : '') +
    (s ? row('当时诊断', `<b>${esc(s.diagnosis?.name || '尚未确诊')}</b> · ${esc(s.symptoms || '症状未记录')}`) +
      (s.outcomeName || s.statusName ? row('当时状态', esc(s.outcomeName || s.statusName)) : '') +
      (s.diagnosis ? row('检查确认', `${date(s.diagnosis.confirmed)} · ${links.hospital(hospitalRef(s.diagnosis.hospital ?? s.diagnosis.facility))}`) : '<p class="meta">只有当时可见症状，没有检查确诊记录。</p>') : '<p class="meta">此条没有保留当时的病例诊断快照，不能据此还原病名或判断死因。</p>') +
    (typeof data.detail === 'string' ? `<p>${esc(data.detail)}</p>` : '') +
    (bill ? row('收款公司', provider) + (bill.moneyUnit === 'cent' ? section('本次已完成服务与实际付款', row('服务', `${esc(bill.service)} · ${date(bill.day)} · 医嘱 #${esc(bill.order)}`) +
      row('医院', links.hospital(hospitalRef(bill.hospital))) + table(['家庭自付', '雇主代付', '基本医保', '补充或工伤保险', '医院实收'], [[paid(bill.patient), paid(bill.employer), paid(bill.public), paid(bill.insurer), paid(bill.total)]]) +
      '<p class="meta">这是本次成功收据，不是全病程总费用；没有收据的旧记录不反推收费。</p>') : '<p class="meta">此记录没有可确认的账单单位，付款金额请看原始资料，不换算猜测。</p>') : '');
}
